import { useId } from 'react';
import type { TransferClientOption, TransferStatus } from '@baex/shared';
import { ROOT_SUBFOLDER_LABEL } from '../../lib/transferLabels';
import { SelectField, TextField } from '../../components/ui/Field';

/** Filter state held by the Transfers page (see the "Filter bar" section). */
export interface TransferFilterState {
  client: string;
  subfolder: string;
  status: 'all' | TransferStatus;
  from: string;
  to: string;
  q: string;
}

export const DEFAULT_FILTERS: TransferFilterState = {
  client: 'all',
  subfolder: 'all',
  status: 'all',
  from: '',
  to: '',
  q: '',
};

/**
 * The Status options, in the fixed order the spec sets out (see the "Filter bar" section): the rough lifecycle,
 * not the alphabet — a file is `stored` (or its upload `replayed`), gets `listed`, is
 * eventually `deleted`; `error` stands apart. `listed` has to be here: a client polling
 * once a minute writes a `listed` row a minute, so without it those rows bury everything
 * anyone actually came to look at.
 */
export const STATUS_OPTIONS: ReadonlyArray<TransferFilterState['status']> = [
  'all',
  'stored',
  'replayed',
  'listed',
  'deleted',
  'error',
];

export const DATE_RANGE_ERROR = "'From' date must be on or before 'To' date.";

/** The filter is simply not applied while the range is inverted (see the "Filter bar" section). */
export function dateRangeError(filters: TransferFilterState): string | null {
  if (filters.from && filters.to && filters.from > filters.to) return DATE_RANGE_ERROR;
  return null;
}

export interface FilterBarProps {
  filters: TransferFilterState;
  onChange: (next: TransferFilterState) => void;
  /**
   * Every Client ID the history mentions, sorted alphabetically - including clients that
   * have since been disabled or deleted (see the "Filter bar" section).
   */
  clientOptions: TransferClientOption[];
  /**
   * Every distinct subfolder in the history, scoped to the selected client, `(root)` first.
   * It describes the whole history, not the rows currently on screen (see the "Filter bar" section).
   */
  subfolders: string[];
}

/**
 * A client that no longer exists (or is disabled) is labelled as such, so an admin is not
 * left wondering why a Client ID in this list is missing from the Clients screen.
 */
export function clientOptionLabel(option: TransferClientOption): string {
  return option.status === 'active' ? option.clientId : `${option.clientId} (${option.status})`;
}

export function FilterBar({ filters, onChange, clientOptions, subfolders }: FilterBarProps) {
  const errorId = useId();
  const rangeError = dateRangeError(filters);

  function set<K extends keyof TransferFilterState>(key: K, value: TransferFilterState[K]) {
    const next = { ...filters, [key]: value };
    // Changing the client narrows the subfolder list, so an out-of-scope pick is dropped.
    if (key === 'client') next.subfolder = 'all';
    onChange(next);
  }

  return (
    <section
      aria-label="Filters"
      className="shrink-0 rounded border border-hairline bg-surface px-3 py-3"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <SelectField
          label="Client"
          value={filters.client}
          onChange={(event) => set('client', event.target.value)}
        >
          <option value="all">All</option>
          {clientOptions.map((option) => (
            <option key={option.clientId} value={option.clientId}>
              {clientOptionLabel(option)}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Subfolder"
          value={filters.subfolder}
          onChange={(event) => set('subfolder', event.target.value)}
        >
          <option value="all">All</option>
          {subfolders.map((subfolder) => (
            <option key={subfolder} value={subfolder}>
              {subfolder === '' ? ROOT_SUBFOLDER_LABEL : subfolder}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Status"
          value={filters.status}
          onChange={(event) => set('status', event.target.value as TransferFilterState['status'])}
        >
          {/* Lifecycle order, not alphabetical (see the "Filter bar" section) — see STATUS_OPTIONS. */}
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status === 'all' ? 'All' : status}
            </option>
          ))}
        </SelectField>

        {/*
          `invalid` rather than an extra `border-danger` class: layered on, the neutral
          border won and these two fields drew an ordinary outline while the message
          underneath said they were wrong. One condition, one shared component (see the "Admin Interface" section).
          The message is shared, so it is named by `aria-describedby` on both.
        */}
        <TextField
          label="From"
          type="date"
          value={filters.from}
          onChange={(event) => set('from', event.target.value)}
          invalid={Boolean(rangeError)}
          aria-describedby={rangeError ? errorId : undefined}
        />
        <TextField
          label="To"
          type="date"
          value={filters.to}
          onChange={(event) => set('to', event.target.value)}
          invalid={Boolean(rangeError)}
          aria-describedby={rangeError ? errorId : undefined}
        />

        {/*
          The hint used to repeat the placeholder in different words, and being the only
          control in the row with a second line it also set the height of the whole bar —
          a band of empty space under the other five. One string, in the placeholder.
        */}
        <TextField
          label="Search"
          type="search"
          placeholder="filename, client or request id"
          value={filters.q}
          onChange={(event) => set('q', event.target.value)}
        />
      </div>

      {rangeError ? (
        <p id={errorId} role="alert" className="mt-2 text-2xs text-danger">
          {rangeError}
        </p>
      ) : null}
    </section>
  );
}
